import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual, isNil } from 'lodash';

import {
  getCustomerPayrollCycleLabel,
  getCustomerInvoicingCycleLabel,
  getCommissionTypeApprovalLabel,
  getCustomerPaymentTermsLabel,
} from 'utils/clientUtils';
import {
  CustomerDocumentTypeEnum,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import BaseAlert from 'components/wrappers/BaseAlert';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getCustomerHistoryFile } from 'redux/reducers/clientSlice';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import { getYesNoAnswer } from 'components/ActivitiesDetailsSection/HistorySection/utils';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import { formatNumberAsCurrency } from 'utils/numberUtils';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  customerId: string;
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function FinancialInfoDetailsCard(props: Props) {
  const { historyDetails, customerId, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  // Rate Overview
  const hasRateOverviewChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview,
    newData?.additionalInfo?.financialInformation?.rateOverview,
  );
  const hasFixedMarkupRateChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview
      ?.fixedMarkupRate,
    newData?.additionalInfo?.financialInformation?.rateOverview
      ?.fixedMarkupRate,
  );
  const hasDTBillRateMultiplierChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview
      ?.dtBillRateMultiplier,
    newData?.additionalInfo?.financialInformation?.rateOverview
      ?.dtBillRateMultiplier,
  );
  const hasOTRateMultiplierChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview
      ?.otRateMultiplier,
    newData?.additionalInfo?.financialInformation?.rateOverview
      ?.otRateMultiplier,
  );

  const hasTempToPermConversionFileKeyChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.rateOverview
      ?.tempToPermConversionFileKey,
    newData?.additionalInfo?.financialInformation?.rateOverview
      ?.tempToPermConversionFileKey,
  );
  /// MSP/VMS overview
  const hasMspVmsOverviewChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview,
  );
  const hasMspFeeChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.mspFee,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.mspFee,
  );
  const hasVmsFeeChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.vmsFee,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.vmsFee,
  );
  const hasInvoicingInformationChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.invoicingInformation,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.invoicingInformation,
  );
  const hasSpecialInvoicingInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.specialInvoicingInstructions,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.specialInvoicingInstructions,
  );
  const hasInvoicingCycleChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.invoicingCycle,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.invoicingCycle,
  );
  const hasPayrollCycleChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.payrollCycle,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.payrollCycle,
  );
  const hasIsThereThirdPartyWorkforceChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.isThereThirdPartyWorkforce,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.isThereThirdPartyWorkforce,
  );
  const hasURLChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.url,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview?.url,
  );
  const hasCredentialsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.credentials,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.credentials,
  );
  const hasMspVmsProviderNameChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.mspOrVmsProviderName,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.mspOrVmsProviderName,
  );
  const hasPurchaseOrderChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.purchaseOrder,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.purchaseOrder,
  );
  const hasPurchaseOrderInstructionsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.purchaseOrderInstructions,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.purchaseOrderInstructions,
  );
  const hasPurchaseOrderFileKeyChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.purchaseOrderFileKey,
    newData?.additionalInfo?.financialInformation?.mspOrVmsOverview
      ?.purchaseOrderFileKey,
  );
  // Financial overview
  const hasFinancialOverviewChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview,
    newData?.additionalInfo?.financialInformation?.financialOverview,
  );
  const hasCreditCheckScoreChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.creditCheckScore,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.creditCheckScore,
  );
  const hasCommisionTypeApprovalChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.commissionTypeApproval,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.commissionTypeApproval,
  );
  const hasAddedNotesChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.addedNotes,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.addedNotes,
  );
  const hasPaymentTermsChanged = !isEqual(
    oldData?.additionalInfo?.financialInformation?.financialOverview
      ?.paymentTerm,
    newData?.additionalInfo?.financialInformation?.financialOverview
      ?.paymentTerm,
  );

  const handleFileDownload = async (
    fileKey: string,
    documentType: CustomerDocumentTypeEnum,
  ) => {
    const result = await dispatch(
      getCustomerHistoryFile({
        customerId,
        fileKey,
        documentType,
      }),
    );

    if (getCustomerHistoryFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const renderRateOverviewSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'rate-overview';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Rate overview</div>
          </Col>

          {hasDTBillRateMultiplierChanged && (
            <HistoryField
              title="DT bill rate multiplier"
              value={formatNumberAsCurrency({
                num: customerDetails?.additionalInfo?.financialInformation
                  ?.rateOverview?.dtBillRateMultiplier,
              })}
              dataId={`${sectionName}-DT-bill-rate-multiplier-field`}
            />
          )}

          {hasFixedMarkupRateChanged && (
            <HistoryField
              title="Fixed markup rate"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.rateOverview?.fixedMarkupRate
              }
              dataId={`${sectionName}-markup-rate-field`}
            />
          )}

          {hasOTRateMultiplierChanged && (
            <HistoryField
              title="OT Rate multiplier"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.rateOverview?.otRateMultiplier
              }
              dataId={`${sectionName}-ot-rate-multiplier-field`}
            />
          )}

          {hasTempToPermConversionFileKeyChanged && (
            <HistoryField
              title="Temp-to-perm conversion"
              emptyText="No file"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.rateOverview?.tempToPermConversionFileKey &&
                customerDetails?.additionalInfo?.financialInformation
                  ?.rateOverview?.tempToPermConversionFileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleFileDownload(
                        customerDetails?.additionalInfo?.financialInformation
                          ?.rateOverview?.tempToPermConversionFileKey as string,
                        CustomerDocumentTypeEnum.TempToPermConversion,
                      )
                    }
                  >
                    {getFileNameWithoutExt(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.rateOverview?.tempToPermConversionFileName || '',
                    )}
                  </span>
                )
              }
              dataId={`${sectionName}-temp-to-perm-conversion-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderMspVmsOverviewSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'MSP-VMS-overview';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>MSP/VMS overview</div>
          </Col>
          {hasMspFeeChanged && (
            <HistoryField
              title="MSP fee"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.mspOrVmsOverview?.mspFee,
                )
                  ? `${customerDetails?.additionalInfo?.financialInformation?.mspOrVmsOverview?.mspFee?.toFixed(
                      2,
                    )}%`
                  : undefined
              }
              dataId={`${sectionName}-MSP-fee-field`}
            />
          )}
          {hasVmsFeeChanged && (
            <HistoryField
              title="VMS fee"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.mspOrVmsOverview?.vmsFee,
                )
                  ? `${customerDetails?.additionalInfo?.financialInformation?.mspOrVmsOverview?.vmsFee?.toFixed(
                      2,
                    )}%`
                  : undefined
              }
              dataId={`${sectionName}-VMS-fee-field`}
            />
          )}
          {hasInvoicingInformationChanged && (
            <HistoryField
              title="Invoicing information"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.invoicingInformation
              }
              dataId={`${sectionName}-invoicing-information-field`}
            />
          )}
          {hasSpecialInvoicingInstructionsChanged && (
            <HistoryField
              title="Special invoicing instructions"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.specialInvoicingInstructions
              }
              dataId={`${sectionName}-special-invoicing-instructions-field`}
            />
          )}
          {hasInvoicingCycleChanged && (
            <HistoryField
              title="Invoicing cycle"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.mspOrVmsOverview?.invoicingCycle,
                )
                  ? getCustomerInvoicingCycleLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.mspOrVmsOverview?.invoicingCycle,
                    )
                  : undefined
              }
              dataId={`${sectionName}-invoicing-cycle-field`}
            />
          )}
          {hasPayrollCycleChanged && (
            <HistoryField
              title="Pay period"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.mspOrVmsOverview?.payrollCycle,
                )
                  ? getCustomerPayrollCycleLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.mspOrVmsOverview?.payrollCycle,
                    )
                  : undefined
              }
              dataId={`${sectionName}-payroll-cycle-field`}
            />
          )}
          {hasIsThereThirdPartyWorkforceChanged && (
            <HistoryField
              title="Is there a third party workforce?"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.isThereThirdPartyWorkforce,
              )}
              dataId={`${sectionName}-is-there-third-party-workforce-field`}
            />
          )}
          {hasURLChanged && (
            <HistoryField
              title="URL"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.url
              }
              dataId={`${sectionName}-URL-field`}
            />
          )}
          {hasCredentialsChanged && (
            <HistoryField
              title="Credentials"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.credentials
              }
              dataId={`${sectionName}-credentials-field`}
            />
          )}
          {hasMspVmsProviderNameChanged && (
            <HistoryField
              title="MSP/VMS provider name"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.mspOrVmsProviderName
              }
              dataId={`${sectionName}-provider-name-field`}
            />
          )}
          {hasPurchaseOrderChanged && (
            <HistoryField
              title="Purchase order"
              value={getYesNoAnswer(
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.purchaseOrder,
              )}
              dataId={`${sectionName}-purchase-order-field`}
            />
          )}
          {hasPurchaseOrderInstructionsChanged && (
            <HistoryField
              title="Purchase order instructions"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.purchaseOrderInstructions
              }
              dataId={`${sectionName}-purchase-order-instructions-field`}
            />
          )}
          {hasPurchaseOrderFileKeyChanged && (
            <HistoryField
              title="Purchase order"
              emptyText="No file"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.purchaseOrderFileKey &&
                customerDetails?.additionalInfo?.financialInformation
                  ?.mspOrVmsOverview?.purchaseOrderFileName && (
                  <span
                    className="link-imitation"
                    onClick={() =>
                      handleFileDownload(
                        customerDetails?.additionalInfo?.financialInformation
                          ?.mspOrVmsOverview?.purchaseOrderFileKey as string,
                        CustomerDocumentTypeEnum.PurchaseOrder,
                      )
                    }
                  >
                    {getFileNameWithoutExt(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.mspOrVmsOverview?.purchaseOrderFileName || '',
                    )}
                  </span>
                )
              }
              dataId={`${sectionName}-purchase-order-file-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderFinancialOverviewSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'financial-overview';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Financial overview</div>
          </Col>

          {hasCreditCheckScoreChanged && (
            <HistoryField
              title="Credit check score"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.creditCheckScore
              }
              dataId={`${sectionName}-credit-check-score-field`}
            />
          )}

          {hasCommisionTypeApprovalChanged && (
            <HistoryField
              title="Commission type approval"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.financialOverview?.commissionTypeApproval,
                )
                  ? getCommissionTypeApprovalLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.commissionTypeApproval,
                    )
                  : undefined
              }
              dataId={`${sectionName}-commission-type-approval-field`}
            />
          )}

          {hasAddedNotesChanged && (
            <HistoryField
              title="Added notes"
              value={
                customerDetails?.additionalInfo?.financialInformation
                  ?.financialOverview?.addedNotes
              }
              dataId={`${sectionName}-added-notes-field`}
            />
          )}

          {hasPaymentTermsChanged && (
            <HistoryField
              title="Payment terms"
              value={
                !isNil(
                  customerDetails?.additionalInfo?.financialInformation
                    ?.financialOverview?.paymentTerm,
                )
                  ? getCustomerPaymentTermsLabel(
                      customerDetails?.additionalInfo?.financialInformation
                        ?.financialOverview?.paymentTerm,
                    )
                  : undefined
              }
              dataId={`${sectionName}-payment-terms-field`}
            />
          )}
        </Row>
        <Divider />
      </>
    );
  };

  const renderAdditionalInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasFinancialOverviewChanged &&
          renderFinancialOverviewSection(customerDetails)}

        {hasRateOverviewChanged && renderRateOverviewSection(customerDetails)}

        {hasMspVmsOverviewChanged &&
          renderMspVmsOverviewSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderAdditionalInformation(oldData)}
      newData={renderAdditionalInformation(newData)}
    />
  );
}
